import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type {
  Athlete,
  Competition,
  Discipline,
} from '../../types/domain';
import { individualResultsApi } from '../../api/individualResultsApi';
import { TimeDisplayInput } from '../TimeDisplayInput';
import { NpReasonChips } from './NpReasonChips';

interface Props {
  open: boolean;
  athlete: Athlete | null;
  discipline: Discipline;
  competition: Competition;
  onClose: () => void;
}

export function IndividualResultEntryDialog({
  open,
  athlete,
  discipline,
  competition,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const attemptCount = discipline.attemptsCount ?? 1;
  const threshold = competition.falseStartThreshold ?? 2;

  const [attemptNumber, setAttemptNumber] = useState(1);
  const [timeMs, setTimeMs] = useState<number | null>(null);
  const [isNp, setIsNp] = useState(false);
  const [note, setNote] = useState('');
  const [falseStartThisAttempt, setFalseStartThisAttempt] = useState(false);

  // Vlastní načtení rankingu pro tento dialog — vždy fresh při otevření, žádný stale cache
  const { data: ranking = [] } = useQuery({
    queryKey: ['individual-ranking', discipline.id],
    queryFn: () => individualResultsApi.getIndividualRanking(discipline.id),
    enabled: open,
    refetchOnMount: 'always',
    refetchInterval: open ? 3_000 : false,
  });
  const existingItem = athlete ? ranking.find((r) => r.athleteId === athlete.id) : undefined;

  // Per-pokus counter (z aktuálního rankingu — vrací počet falešných startů přesně pro tento attempt)
  const previousFalseStartCount =
    existingItem?.falseStartCountByAttempt?.[attemptNumber - 1] ?? 0;
  // Předpokládaný stav po uložení
  const projectedFalseStartCount = previousFalseStartCount + (falseStartThisAttempt ? 1 : 0);
  const willDisqualify = falseStartThisAttempt && projectedFalseStartCount >= threshold;

  // Reset stavu při otevření / změně pokusu / atleta
  useEffect(() => {
    if (!open) return;
    if (existingItem) {
      const idx = attemptNumber - 1;
      const t = existingItem.attemptTimesMs[idx] ?? null;
      setTimeMs(t);
      setIsNp(existingItem.isNp);
      setNote(existingItem.note ?? '');
    } else {
      setTimeMs(null);
      setIsNp(false);
      setNote('');
    }
    setFalseStartThisAttempt(false);
  }, [open, attemptNumber, existingItem, athlete?.id]);

  const saveMut = useMutation({
    mutationFn: () => {
      if (!athlete) return Promise.reject(new Error('Žádný závodník'));
      return individualResultsApi.saveResult(discipline.id, {
        athleteId: athlete.id,
        attemptNumber,
        timeMs: isNp || willDisqualify ? null : timeMs,
        isNp: isNp || willDisqualify,
        note: note || undefined,
        falseStart: falseStartThisAttempt,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['individual-ranking', discipline.id] });
      qc.invalidateQueries({ queryKey: ['team-sum-ranking', discipline.id] });
      enqueueSnackbar('Výsledek uložen', { variant: 'success' });
      onClose();
    },
    onError: (e: any) =>
      enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při ukládání', { variant: 'error' }),
  });

  if (!athlete) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {athlete.lastName} {athlete.firstName} — {discipline.name}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {Array.from({ length: attemptCount }, (_, i) => (
              <Button
                key={i}
                variant={attemptNumber === i + 1 ? 'contained' : 'outlined'}
                onClick={() => setAttemptNumber(i + 1)}
                size="small"
              >
                Pokus {i + 1}
              </Button>
            ))}
          </Box>

          <Divider>Start</Divider>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={falseStartThisAttempt}
                  onChange={(e) => setFalseStartThisAttempt(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Falešný start</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Po uložení se počet falešných startů v tomto pokusu zvýší o 1.
                    Při {threshold}. falešném startu v jednom pokusu = neplatný pokus.
                  </Typography>
                </Box>
              }
            />
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                ml: 4,
                color: willDisqualify ? 'error.main' : 'text.secondary',
                fontWeight: willDisqualify ? 700 : 400,
              }}
            >
              Pokus {attemptNumber}: {previousFalseStartCount} falešných startů dosud
              {falseStartThisAttempt && ` → po uložení ${projectedFalseStartCount}/${threshold}`}
              {willDisqualify && ' — NEPLATNÝ POKUS'}
            </Typography>
          </Box>

          <Divider>Čas</Divider>
          <TimeDisplayInput
            format={discipline.timeFormat ?? 'SS_CC'}
            valueMs={timeMs}
            onChangeMs={(ms) => setTimeMs(ms)}
            disabled={isNp || willDisqualify}
          />

          <FormControlLabel
            control={<Checkbox checked={isNp} onChange={(e) => setIsNp(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2">Neplatný pokus</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pokus byl proveden ale je neplatný (např. pád z kladiny, vynechání bariéry).
                </Typography>
              </Box>
            }
          />

          {(isNp || willDisqualify) && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Důvod (klikni na chip pro doplnění):
              </Typography>
              <NpReasonChips onPick={(r) => setNote((n) => (n ? `${n}; ${r}` : r))} />
            </Box>
          )}

          <TextField
            label="Poznámka"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zavřít</Button>
        <Button
          variant="contained"
          onClick={() => saveMut.mutate()}
          disabled={
            saveMut.isPending ||
            (!isNp && !willDisqualify && !falseStartThisAttempt && timeMs == null)
          }
        >
          Uložit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
